import { useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const faqGroups = [
  {
    title: '阅读与使用',
    icon: 'ri-book-open-line',
    items: [
      { q: '如何快速找到想看的漫画？', a: '你可以使用首页搜索框输入漫画名或作者关键词，也可以进入"漫画分类"页面，按爱情、奇幻、动作、校园、悬疑等类型筛选，还能结合高分与人气排序快速定位。' },
      { q: '网站每天什么时候更新漫画？', a: '热门连载作品一般会在每天 18:00 后陆续更新，节假日可能有小幅延迟。遇到更新时刷新页面即可看到最新章节。' },
      { q: '收藏和评分有什么作用？', a: '收藏功能方便你随时回到追更列表；评分则用于计算作品的人气与榜单排名，你的每一次评分都在帮助其他读者发现好作品。' },
    ],
  },
  {
    title: '内容与版权',
    icon: 'ri-shield-check-line',
    items: [
      { q: '韩漫星球是免费阅读的吗？', a: '目前大部分作品提供免费试读与部分章节，具体以每部作品的展示为准。我们始终尊重版权，仅展示授权范围内的内容。' },
      { q: '可以推荐漫画吗？', a: '当然可以。你可以在相关页面的"编辑精选"板块看到我们的推荐逻辑，也欢迎通过页面底部邮箱向我们投稿你心中的好作品。' },
      { q: '如果发现内容有误该如何反馈？', a: '请通过页面底部提供的联系方式向我们反馈，我们会尽快核实并修正分类、简介或章节信息。' },
    ],
  },
  {
    title: '账号与技术',
    icon: 'ri-device-line',
    items: [
      { q: '支持哪些设备阅读？', a: '网站采用响应式设计，支持电脑、平板和手机。你可以跨设备随时切换，阅读进度会自动记住。' },
      { q: '加载慢或无法打开页面怎么办？', a: '建议先刷新页面或更换网络环境重试。如果问题持续，请通过邮箱联系我们并附上设备与浏览器信息，我们会尽快协助解决。' },
      { q: '搜索结果为空怎么办？', a: '可以尝试使用更简短的关键词，或者减少限定条件。若仍无法找到，可浏览"全部漫画"按分类慢慢发现。' },
    ],
  },
];

export default function Faq() {
  const [openGroup, setOpenGroup] = useState<string | null>(faqGroups[0].title);
  const [openItem, setOpenItem] = useState<string | null>(`${faqGroups[0].title}-0`);

  const toggleGroup = (title: string) => setOpenGroup((cur) => (cur === title ? null : title));
  const toggleItem = (key: string) => setOpenItem((cur) => (cur === key ? null : key));

  const allFaqs = faqGroups.flatMap((g) => g.items);

  return (
    <>
      <Seo
        title="常见问题 - 韩漫星球 | 韩国漫画网"
        description="关于韩漫星球阅读使用、内容版权、账号技术的常见问题解答，帮助你更快上手追漫，找到想看的每一部韩国漫画。"
        keywords="韩漫星球,常见问题,漫画阅读,韩国漫画网,使用帮助"
        canonicalPath="/faq"
        type="website"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: allFaqs.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: typeof window !== 'undefined' ? window.location.origin : '/' },
              { '@type': 'ListItem', position: 2, name: '常见问题', item: typeof window !== 'undefined' ? window.location.href : '/faq' },
            ],
          },
        ]}
      />

      <Navbar />

      <main className="pt-20 md:pt-24">
        <section className="border-b border-background-200 bg-background-50">
          <div className="mx-auto max-w-4xl px-4 md:px-6 py-10 md:py-14">
            <nav aria-label="面包屑" className="text-sm text-foreground-500">
              <ol className="flex flex-wrap items-center gap-1">
                <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-foreground-800">常见问题</li>
              </ol>
            </nav>
            <h1 className="mt-4 font-heading text-3xl md:text-4xl text-foreground-900">常见问题</h1>
            <p className="mt-3 max-w-xl text-sm leading-7 text-foreground-500">
              关于阅读、更新、版权与账号的问题，这里都有答案。如果仍未解决，欢迎联系我们。
            </p>
          </div>
        </section>

        <section className="bg-background-100 py-12 md:py-16">
          <div className="mx-auto max-w-4xl px-4 md:px-6">
            <div className="flex flex-col gap-6">
              {faqGroups.map((group) => {
                const isGroupOpen = openGroup === group.title;
                return (
                  <div key={group.title} className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.title)}
                      className="flex w-full items-center justify-between gap-3 px-6 py-5 text-left"
                      aria-expanded={isGroupOpen}
                    >
                      <span className="flex items-center gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent-100 text-accent-700">
                          <i className={`${group.icon} text-xl`}></i>
                        </span>
                        <span className="font-heading text-lg text-foreground-900">{group.title}</span>
                      </span>
                      <i className={`ri-arrow-down-s-line text-2xl text-foreground-400 transition-transform ${isGroupOpen ? 'rotate-180' : ''}`}></i>
                    </button>
                    {isGroupOpen && (
                      <div className="border-t border-background-200 px-6 py-2">
                        {group.items.map((item, idx) => {
                          const key = `${group.title}-${idx}`;
                          const isOpen = openItem === key;
                          return (
                            <div key={key} className="border-b border-background-100 last:border-0">
                              <button
                                type="button"
                                onClick={() => toggleItem(key)}
                                className="flex w-full items-center justify-between gap-3 py-4 text-left"
                                aria-expanded={isOpen}
                              >
                                <span className="flex items-center gap-2 text-sm font-semibold text-foreground-800">
                                  <i className="ri-question-line text-primary-500"></i> {item.q}
                                </span>
                                <i className={`ri-add-line text-lg text-foreground-400 transition-transform ${isOpen ? 'rotate-45' : ''}`}></i>
                              </button>
                              {isOpen && <p className="pb-4 pl-6 text-sm leading-7 text-foreground-600">{item.a}</p>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="mt-10 rounded-2xl border border-background-200 bg-background-50 p-8 text-center">
              <h2 className="font-heading text-xl text-foreground-900">仍有疑问？</h2>
              <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-foreground-500">
                你可以通过邮件联系我们，或先去分类页逛逛，说不定已找到想看的漫画。
              </p>
              <div className="mt-5 flex flex-col gap-3 items-center justify-center sm:flex-row">
                <a
                  href="mailto:service@hancomic.example"
                  className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-mail-send-line"></i> 联系我们
                </a>
                <Link
                  to="/category"
                  className="inline-flex items-center gap-2 rounded-full border border-background-300 px-6 py-3 text-sm font-semibold text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
                >
                  浏览全部漫画
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}